import { cn } from '@src/utils/ui';
import parse from 'html-react-parser';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { getBannerSrcSet, getOptimizedImageUrl, handleImageError } from '@src/utils/getOptimizedImageUrl';

const AUTOPLAY_INTERVAL = 5000;
const FADE_DURATION = 500; // ms, matches CSS transition

/** Strips HTML tags and decodes entities to produce clean plain text. */
function stripHtml(html) {
    if (!html) return '';
    const decodeEntities = (str) =>
        str.replace(/&(?:#x([0-9a-f]+)|#([0-9]+)|([a-z]+));/gi, (match, hex, dec, named) => {
            if (hex) return String.fromCharCode(parseInt(hex, 16));
            if (dec) return String.fromCharCode(parseInt(dec, 10));
            const entities = {
                amp: '&', lt: '<', gt: '>',
                quot: '"', apos: "'", nbsp: ' '
            };
            return entities[named] || match;
        });
    // Decode entities (may be double-encoded, so decode twice)
    let text = decodeEntities(decodeEntities(html));
    // Strip all HTML tags
    text = text.replace(/<[^>]*>/gi, '');
    return text.trim();
}

function BannersHome({ panel, user }) {
    const [activeIndex, setActiveIndex] = useState(0);
    const timerRef = useRef(null);
    const slideCountRef = useRef(0);

    const panelDetails = panel
        ? panel.Details.filter(a => a.Title && !a.Title.startsWith('slider_banner_'))
        : [];

    slideCountRef.current = panelDetails.length;

    const startAutoplay = useCallback(() => {
        stopAutoplay();
        timerRef.current = setInterval(() => {
            setActiveIndex(prev => (prev + 1) % slideCountRef.current);
        }, AUTOPLAY_INTERVAL);
    }, []);

    const stopAutoplay = useCallback(() => {
        if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }
    }, []);

    const goToSlide = useCallback((index) => {
        setActiveIndex(index);
        // Reset autoplay timer so it doesn't switch right after user click
        startAutoplay();
    }, [startAutoplay]);

    useEffect(() => {
        if (panelDetails.length > 1) {
            startAutoplay();
        }
        return () => stopAutoplay();
    }, [panelDetails.length, startAutoplay, stopAutoplay]);

    const getBannerImage = (detail) => {
        if (detail != null && detail.PanelMedia != null && detail.PanelMedia.length > 0) {
            return detail.PanelMedia[0].MediaUrl;
        }
        return '';
    };

    const goToBannerUrl = (url) => {
        if (url) {
            window.location = url.startsWith('http') ? url : `${process.env.PROTOCOL}://${url}`;
        }
    };

    const renderCustomButtons = (name) => {
        if (name.includes('Task a')) {
            return (
                <a
                    className={cn([
                        'a-btn a-btn--solid js-interval-reveal o-primary-carousel__cta',
                        '!tw-bg-[#0277d1] !tw-text-[16.25px] !tw-font-[600] !tw-shadow-[0_2px_7px_rgba(0,0,0,0.4)]',
                        '!tw-h-[44.25px]'
                    ])}
                    href='/search'
                    data-sr-id='10'
                    onClick={e => {
                        e.preventDefault();
                        fetch('/track-find-a-tasker', {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' }
                        })
                            .catch(() => { })
                            .finally(() => {
                                window.location.href = '/search';
                            });
                    }}
                >
                    FIND A TASKER
                </a>
            );
        }
        if (name.includes('Enter to')) {
            return (
                <a
                    className='a-btn a-btn--solid a-btn--animated js-interval-reveal o-primary-carousel__cta'
                    href='/pages/promotions'
                    data-sr-id='10'
                >
                    Learn More
                </a>
            );
        }
        if (name.includes('Your guide')) {
            return (
                <a
                    className='a-btn a-btn--solid a-btn--animated js-interval-reveal o-primary-carousel__cta'
                    href='/pages/resources'
                    data-sr-id='10'
                >
                    Learn More
                </a>
            );
        }
        if (name.includes('Thinking about')) {
            return (
                <a
                    className='a-btn a-btn--solid a-btn--animated js-interval-reveal o-primary-carousel__cta'
                    href='https://bcaa.com/getting-your-vehicle-ready-to-sell'
                    data-sr-id='10'
                >
                    READ NOW
                </a>
            );
        }
        if (name.includes('Win a VIP')) {
            return (
                <a
                    className='a-btn a-btn--solid a-btn--animated js-interval-reveal o-primary-carousel__cta'
                    href='http://www.bcaa.com/VancouverCanadiansContest'
                    data-sr-id='10'
                >
                    Learn More
                </a>
            );
        }
        return null;
    };

    const renderCustomConditions = (name) => {
        if (name.includes('Enter to')) {
            return <span className='banner-conditions'>Conditions apply. Ends June 30, 2024. </span>;
        }
        return null;
    };

    const renderCustomConditionsBelow = (name) => {
        if (name.includes('A new way')) {
            return (
                <span className='banner-conditions below'>
                    *BCAA, in association with CAA, was chosen as the #1 Most Trusted Insurance Brand by British
                    Columbia <br />
                    participants in the 2023 Gustavson Brand Trust Index.{' '}
                </span>
            );
        }
        return null;
    };

    const renderSlide = (detail, index) => {
        const bannerTitle = parse(detail.Title || '');
        const bannerDescription = parse(detail.Description || '');
        const buttonTitle = stripHtml(detail.Title);

        // Video slide
        if (detail.Url != null) {
            return (
                <div
                    key={detail.ID || index}
                    className={cn(['banner-slide', index === activeIndex ? 'banner-slide--active' : ''])}
                    aria-hidden={index !== activeIndex ? 'true' : undefined}
                >
                    <video
                        title={stripHtml(detail.Title)}
                        id={detail.ID}
                        autoPlay
                        loop
                        muted
                        playsInline
                    >
                        <source src={detail.Url} type='video/mp4' />
                        Your browser does not support the video tag.
                    </video>
                </div>
            );
        }

        // Image slide
        return (
            <div
                key={detail.ID || index}
                className={cn(['banner-slide', index === activeIndex ? 'banner-slide--active' : ''])}
                aria-hidden={index !== activeIndex ? 'true' : undefined}
            >
                <img
                    // Compensates for SSR hydration timing: image 404s before React attaches onError.
                    ref={(img) => {
                        if (img && img.complete && img.naturalWidth === 0 && !img.dataset.fellBack) {
                            handleImageError({ target: img }, getBannerImage(detail));
                        }
                    }}
                    className={cn([
                        'hero_banner_slider_img',
                        index === panelDetails.length - 1 ? '!tw-object-left md:!tw-object-center' : ''
                    ])}
                    src={getOptimizedImageUrl('banner', detail.ID, index, 'desktop') || getBannerImage(detail)}
                    srcSet={getBannerSrcSet(detail.ID, index) || undefined}
                    sizes='(max-width: 768px) 768px, 1920px'
                    title={stripHtml(detail.Title)}
                    alt={stripHtml(detail.Description)}
                    width={1920}
                    height={667}
                    onError={(e) => handleImageError(e, getBannerImage(detail))}
                    {...(index === 0
                        ? { fetchpriority: 'high', loading: 'eager' }
                        : { loading: 'lazy', decoding: 'async' }
                    )}
                />
                <div className='main-banner-text'>
                    <div className='banner-text'>
                        {parse(bannerTitle)}
                        {parse(bannerDescription)}
                        {renderCustomConditions(buttonTitle)}
                        {renderCustomButtons(buttonTitle)}
                    </div>
                </div>
                {renderCustomConditionsBelow(buttonTitle)}
            </div>
        );
    };

    const renderDots = () => {
        if (panelDetails.length <= 1) return null;

        return (
            <ul className='slick-dots' role='tablist'>
                {panelDetails.map((_, index) => (
                    <li
                        key={index}
                        className={index === activeIndex ? 'slick-active' : ''}
                        role='presentation'
                    >
                        <button
                            type='button'
                            role='tab'
                            aria-label={`Slide ${index + 1} of ${panelDetails.length}`}
                            aria-selected={index === activeIndex}
                            onClick={() => goToSlide(index)}
                        >
                            {index + 1}
                        </button>
                    </li>
                ))}
            </ul>
        );
    };

    // Fallback: no panel data
    if (panel == null) {
        return (
            <div className='home-slider-section'>
                <div className='row-small-gutter hero_banner_slider_show'>
                    <div className='banner-width-update'>
                        <div className='hero_banner_slider gutter-minus-mobile'>
                            <div>
                                <img
                                    className='hero_banner_slider_img'
                                    src='/assets/images/banner.jpg'
                                    alt=''
                                    fetchpriority='high'
                                    loading='eager'
                                    width={1920}
                                    height={667}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className='home-slider-section'>
            <div className='row-small-gutter hero_banner_slider_show'>
                <div className='banner-width-update'>
                    <div className='hero_banner_slider gutter-minus-mobile'>
                        {panelDetails.map((detail, index) => renderSlide(detail, index))}
                        {renderDots()}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default BannersHome;
